import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { fetchAllProducts } from '../store/productsSlice';
import Navbar from '../Components/Navbar';
import ProductCard from '../Components/ProductCard';
import ProductCardSkeleton from '../Components/ProductCardSkeleton';
import Footer from '../Components/Footer';
import HeroSection from '../Components/HeroSection';
import PartnersCarousel from '../Components/PartnersCarousel';
import eyeCalculatorBg from '../Assets/Additional-images/eye-calculator-teaser.jpg';
import voiceSearchBg   from '../Assets/Additional-images/voice-search-teaser.jpg';

function Home() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items: products, status, error } = useSelector((state) => state.products);

  useEffect(() => {
    dispatch(fetchAllProducts());
  }, [dispatch]);

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <Navbar />
      <HeroSection />

      {/* ── Feature Banners ── */}
      <div className="px-4 sm:px-6 lg:px-10 py-10 space-y-6">

        {/* AI Voice Search */}
        <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ minHeight: 300 }}>
          <section className="relative overflow-hidden h-full" style={{ minHeight: 300 }}>
            <img src={voiceSearchBg} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(21,17,11,0.94) 0%, rgba(21,17,11,0.78) 50%, rgba(21,17,11,0.0) 100%)' }} />
            <div className="relative px-8 sm:px-14 py-14 sm:py-18 flex items-center" style={{ minHeight: 300 }}>
              <div className="max-w-lg">
                <span className="inline-flex items-center gap-1.5 text-brand-300 text-xs font-bold uppercase tracking-[0.2em] mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse" />
                  AI-Powered · Smart Search
                </span>
                <h2 className="font-display font-light tracking-tight text-3xl sm:text-4xl md:text-5xl text-cream leading-tight mb-4">
                  Search with<br />
                  <em className="not-italic font-normal italic" style={{ background: 'linear-gradient(90deg,#e9c478,#d4922a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Your Voice
                  </em>
                </h2>
                <p className="text-cream/80 text-sm sm:text-base leading-relaxed mb-8 max-w-sm">
                  Just say what you're looking for — color, brand, price, or duration — and our AI finds the perfect lenses instantly.
                </p>
                <button
                  onClick={() => navigate('/products/colored-lenses')}
                  className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl font-bold text-sm bg-ink text-cream hover:bg-brand-500 hover:text-ink transition-colors duration-200 active:scale-95 hover:scale-[1.03]"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                  Try it now
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* Eye Power Calculator */}
        <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ minHeight: 300 }}>
          <section className="relative overflow-hidden h-full" style={{ minHeight: 300 }}>
            <img src={eyeCalculatorBg} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(270deg, rgba(21,17,11,0.95) 0%, rgba(21,17,11,0.80) 50%, rgba(21,17,11,0.0) 100%)' }} />
            <div className="relative px-8 sm:px-14 py-14 sm:py-18 flex items-center justify-end" style={{ minHeight: 300 }}>
              <div className="max-w-lg text-right">
                <span className="inline-flex items-center justify-end gap-1.5 text-brand-300 text-xs font-bold uppercase tracking-[0.2em] mb-4">
                  Precision Optics · Prescription Guide
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse" />
                </span>
                <h2 className="font-display font-light tracking-tight text-3xl sm:text-4xl md:text-5xl text-cream leading-tight mb-4">
                  Eye Power<br />
                  <em className="not-italic font-normal italic" style={{ background: 'linear-gradient(90deg,#e9c478,#d4922a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Calculator
                  </em>
                </h2>
                <p className="text-cream/80 text-sm sm:text-base leading-relaxed mb-8 max-w-sm ml-auto">
                  Enter your prescription or upload a photo — we calculate your contact lens power and show it as a guide on every product page.
                </p>
                <div className="flex justify-end">
                  <Link
                    to="/eye-power-finder"
                    className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl font-bold text-sm bg-ink text-cream hover:bg-brand-500 hover:text-ink transition-colors duration-200 active:scale-95 hover:scale-[1.03]"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" /><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                    </svg>
                    Calculate now
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>

      </div>

      {/* ── Best Sellers ── */}
      <section className="bg-white mx-4 sm:mx-6 lg:mx-10 mb-10 rounded-2xl border border-sand shadow-card hover:shadow-card-hover px-6 pt-12 pb-10">
        <div className="text-center mb-10">
          <p className="text-brand-600 text-xs font-bold uppercase tracking-widest mb-2">
            Top Picks
          </p>
          <h2 className="font-display font-light tracking-tight text-2xl sm:text-3xl md:text-4xl text-ink">
            Best Sellers
          </h2>
          <div className="mt-3 mx-auto w-12 h-1 rounded-full bg-brand-500" />
        </div>

        {/* Loading skeleton */}
        {status === 'loading' && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Error state */}
        {status === 'failed' && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-ink mb-1">Failed to load products</p>
            <p className="text-clay text-sm mb-4">{error || 'Something went wrong'}</p>
            <button
              onClick={() => dispatch(fetchAllProducts())}
              className="px-5 py-2.5 bg-ink text-cream hover:bg-brand-500 hover:text-ink rounded-xl font-semibold text-sm transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Products grid */}
        {status === 'succeeded' && products.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {products.map((product, index) => (
              <div
                key={product._id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${Math.min(index, 7) * 50}ms` }}
              >
                <ProductCard
                  id={product._id}
                  productName={product.name}
                  currentPrice={product.hasDiscount ? product.priceAfterDiscount : product.price}
                  oldPrice={product.hasDiscount ? product.price : null}
                  productImageUrl={product.images[0]}
                  productHoverImageUrl={product.images[1] || product.images[0]}
                  category={product.category}
                  duration={product.duration}
                  rating={product.ratingsAverage}
                  ratingsCount={product.ratingsQuantity}
                  isNew={product.subCategory === '69d0721768659c2e081b4b62'}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Our Partners ── */}
      <section className="bg-white mx-4 sm:mx-6 lg:mx-10 mb-10 rounded-2xl border border-sand shadow-card hover:shadow-card-hover py-14">
        <div className="text-center mb-8">
          <p className="text-brand-600 text-xs font-bold uppercase tracking-widest mb-2">
            Trusted Brands
          </p>
          <h2 className="font-display font-light tracking-tight text-2xl sm:text-3xl md:text-4xl text-ink">
            Our Partners
          </h2>
          <div className="mt-3 mx-auto w-12 h-1 rounded-full bg-brand-500" />
        </div>
        <PartnersCarousel />
      </section>

      <Footer />
    </div>
  );
}

export default Home;
